import { createAsyncThunk } from '@reduxjs/toolkit'

import {
  AffectToUsage,
  AssetIdentifier,
  CreateDefectFormData,
  CreateDefectPayload,
  CreateDefectResponse,
  DefectGroup,
  DefectTree,
  ItemData,
  ItemOptions,
  LimitationOfUsage,
  OptionTypes,
} from '../../components/create-defect/types'
import { apiGET, apiPOST } from '../../lib/api'
import { createFetchAction } from '../defect-common'

interface CreateDefectError {
  message: string
}

const formatPayload = (payload: CreateDefectFormData): CreateDefectPayload => {
  const installationItem =
    payload.type === 'equipment' ? payload.vehicleType : payload.installationItem
  const serialNumber =
    payload.type === 'equipment' ? payload.vehicleSerialNumber : payload.installationSerialNumber
  return {
    affectToUsage: payload.affectToUsage,
    comment: payload.comment,
    criticality: +payload.criticality,
    defectGroup: payload.defectGroup,
    digitalAssetUuids: payload.digitalAssetUuids,
    installationGroup: payload.installationGroup,
    installationItem: `         ${installationItem}`, // spaces are intentional
    installationSerialNumber: serialNumber,
    issuedTime: payload.issuedTime.toISOString().split('.')[0] + 'Z',
    reportedDefectCode: payload.defectCode,
    restrictionCode: payload.restrictionCode,
  }
}

const createDefect = (payload: CreateDefectPayload): Promise<CreateDefectResponse> =>
  apiPOST('defect', payload)

export const createDefectAction = createAsyncThunk<
  { response: CreateDefectResponse; request: CreateDefectPayload },
  CreateDefectFormData | undefined,
  { rejectValue: CreateDefectError }
>('defect/createDefect', async (payload, thunkApi) => {
  try {
    if (!payload) {
      return thunkApi.rejectWithValue({
        message: 'Failed to create defect',
      })
    }
    const formattedPayload = formatPayload(payload)
    const response = await createDefect(formattedPayload)

    if (!response.id) {
      return thunkApi.rejectWithValue({
        message: 'Failed to create defect',
      })
    }

    return { response, request: formattedPayload }
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to create defect',
    })
  }
})

const detectImageMimeFromBase64 = (base64: string): string | null => {
  try {
    const head = atob(base64.slice(0, 32))
    const code = (i: number) => head.charCodeAt(i)
    if (code(0) === 0xff && code(1) === 0xd8 && code(2) === 0xff) return 'image/jpeg'
    if (code(0) === 0x89 && code(1) === 0x50 && code(2) === 0x4e && code(3) === 0x47)
      return 'image/png'
    if (code(0) === 0x47 && code(1) === 0x49 && code(2) === 0x46 && code(3) === 0x38)
      return 'image/gif'
    if (head.slice(0, 4) === 'RIFF' && head.slice(8, 12) === 'WEBP') return 'image/webp'
    return null
  } catch {
    return null
  }
}

const file2Base64 = (file: File): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const raw = reader.result?.toString()
      if (raw) {
        const withoutMetaDataType = raw.split(',')[1]
        resolve(withoutMetaDataType)
      } else {
        reject(new Error('Failed to convert file to base64'))
      }
    }
    reader.onerror = (error) => reject(error)
  })
}

const postDigitalAsset = async (
  payload: File,
  uuid: string
): Promise<{ response: string; base64: string }> => {
  const path = `digitalAssets?uuid=${uuid}&filename=${payload.name}`
  const base64 = await file2Base64(payload)
  if (base64 === null) {
    throw new Error('file2Base64 returned null')
  }

  const response = await apiPOST<string>(path, { base64 })
  return { response, base64 }
}

export type PostDigitalAssetResult = AssetIdentifier & {
  base64: string
  mimeType: string
}

export const postDigitalAssetAction = createAsyncThunk<
  PostDigitalAssetResult,
  File,
  { rejectValue: CreateDefectError }
>('defect/postDigitalAsset', async (payload: File, thunkApi) => {
  try {
    const id = crypto.randomUUID()
    const { response, base64 } = await postDigitalAsset(payload, id)

    if (response !== 'OK') {
      return thunkApi.rejectWithValue({
        message: response,
      })
    }

    const detectedMime = detectImageMimeFromBase64(base64)
    return {
      id,
      filename: payload.name,
      base64,
      mimeType: detectedMime ?? payload.type ?? 'application/octet-stream',
    }
  } catch (err) {
    return thunkApi.rejectWithValue({
      message: 'Failed to send digital asset',
    })
  }
})

interface OptionFetch<T extends keyof OptionTypes> {
  id: T
  data: OptionTypes[T]
}

export type OptionsWithID<T extends keyof OptionTypes> = Promise<OptionFetch<T>>

const getItems = async ({
  filter,
  itemId,
}: {
  filter: string
  itemId: string
}): OptionsWithID<'items'> => {
  const response = await apiGET<{
    physicalBreakdowns: Omit<ItemData, 'uniqueId' | 'parentUniqueId'>[]
  }>(`physicalBreakdown?serialNumber=${filter}&itemId=${itemId}`)
  const dataArray = response.physicalBreakdowns.map(
    ({ itemId, serialNumber, parentItemId, parentSerialNumber, description }) => ({
      itemId,
      serialNumber,
      parentItemId,
      parentSerialNumber,
      description,
      uniqueId: `${itemId}|${serialNumber}`,
      parentUniqueId: `${parentItemId}|${parentSerialNumber}`,
      seperator: '|' as const,
    })
  )
  const data: ItemOptions = {}
  dataArray.forEach((item) => {
    data[item.uniqueId] = item
  })
  return { data, id: 'items' }
}

const getLimitationsOfUsage = async ({
  serializedItemIds,
}: {
  serializedItemIds: string[]
}): OptionsWithID<'limitationsOfUsage'> => {
  const response = await apiGET<{ limitationsOfUsage: LimitationOfUsage[] }>(
    `limitationsOfUsage?serializedItemIds=${serializedItemIds.join(',')}`
  )
  const data = response.limitationsOfUsage.map(({ id, description }) => ({
    id,
    description,
  }))
  return { data, id: 'limitationsOfUsage' }
}

const getDefects = async (itemId: string): OptionsWithID<'defectTrees'> => {
  const response = await apiGET<{ defectTrees: DefectTree[] }>(`defectTrees?itemId=${itemId}`)
  const data = response.defectTrees.map(
    ({
      productGroups,
      defectType,
      criticality,
      defectCode,
      mpgDescription,
      spgDescription,
      vrpg1Description,
      vrpg2Description,
    }) => ({
      productGroups,
      defectType,
      criticality,
      defectCode,
      mpgDescription,
      spgDescription,
      vrpg1Description,
      vrpg2Description,
    })
  )
  return { data, id: 'defectTrees' }
}

const getAffectToUsage = async (): OptionsWithID<'affectToUsage'> => {
  const response = await apiGET<{ codes: AffectToUsage[] }>('codes?group=affecttousage')
  const data = response.codes.map(({ id, description }) => ({
    id,
    description,
  }))
  return { data, id: 'affectToUsage' }
}

const getDefectGroups = async (): OptionsWithID<'defectGroups'> => {
  const response = await apiGET<{ defectGroups: DefectGroup[] }>('defectGroups')
  const groupsOfInterest = response.defectGroups
    .filter((group) => group.useForCreateDefect)
    .sort((a, b) => a.sortIndex - b.sortIndex)
  const data = groupsOfInterest.map(
    ({ id, description, useForCreateDefect, createDefectRequirePicture, type, sortIndex }) => ({
      id,
      description,
      useForCreateDefect,
      createDefectRequirePicture,
      type,
      sortIndex,
    })
  )
  return { data, id: 'defectGroups' }
}

const fetchDefectGroupsAction = createFetchAction('defectGroups', getDefectGroups)
const fetchItemsAction = createFetchAction('items', getItems)
const fetchDefectTreesAction = createFetchAction('defects', getDefects)
const fetchLimitationsOfUsageAction = createFetchAction('limitationsOfUsage', getLimitationsOfUsage)
const fetchAffectToUsageAction = createFetchAction('affectToUsage', getAffectToUsage)

export const options = {
  fetchItemsAction,
  fetchDefectTreesAction,
  fetchLimitationsOfUsageAction,
  fetchAffectToUsageAction,
  fetchDefectGroupsAction,
}
